import { MyMenuContent, MyMenuExternalUrlMap } from '@/constant/mymenu';
import { useResetAtom } from '@/hooks/jotai/useResetAtom';
import { MyInfoState } from '@/recoil/atom';
import { INIT_MY_INFO } from '@/recoil/init_data';
import { MyMenuDrawerProps } from '@/components/MenuDrawer/MyMenuDrawer';
import { useFindPassport, useGetVacationStatus } from '@/hooks/apiHooks';
import { useRouter } from 'next/router';
import { disableMessage } from '@/utils/firebase/initialize';
import { Language, LanguageValue, MyInfo } from '@/types/api';
import { MyPageMainProps } from '@/components/MenuDrawer/MyMenuDrawer/Main';
import { MyMenuDrawerSettings } from '@/components/MenuDrawer/MyMenuDrawer/@hooks/useMyMenuDrawerProps';
import { useMemo } from 'react';
import { useDialog } from '@/hooks/global-tools';
import { AxiosError } from 'axios';
import useFarmCongestion from '@/components/MenuDrawer/MyMenuDrawer/SubMenu/Olive/Congestion/@hook/useFarmCongestion';
import {
  isRnWebView,
  openExternalUrl,
  RnMessage,
  sendToRN,
} from '@/utils/function';
import { PAGE_URL } from '@/constant/url';
import { useTranslation } from 'react-i18next';
import { cleanupUserSession } from '@/utils/auth/cleanup';
import { WORKING_HOURS_OPTIONS } from '@/components/MenuDrawer/MyMenuDrawer/SubMenu/WorkingHoursChange/@hooks/useWorkingHoursChangeProps';
import useSWR from 'swr';
import { getNestedDataFetcher } from '@/utils/apis';
import {
  formatLeaveMinutes,
  vacationGrantedMinutes,
  vacationRemainingMinutes,
} from '@/utils/leave';

const useMainProps = ({
  myInfo,
  onClose,
  openSubMenu,
  isOpen = false,
}: {
  isOpen?: boolean;
  myInfo: MyInfo;
  openSubMenu: MyMenuDrawerProps['openSubMenu'];
  onClose: MyMenuDrawerSettings['onClose'];
}): MyPageMainProps => {
  const { t } = useTranslation();
  const { passportId, departmentName, divisionName, language, workingHours } =
    myInfo;

  const { data: passportData, isLoading } = useFindPassport(
    isOpen ? passportId : '',
  );
  const { data: vacationData, error } = useGetVacationStatus(
    isOpen ? passportId : '',
  );
  console.log(vacationData);

  const { currentStatus } = useFarmCongestion(isOpen);

  // 연장근무 내역 조회 메뉴는 A, HC 법인에만 노출 → 해당 법인일 때만 사용 가능 시간 조회
  const isOvertimeVisible = ['A', 'HC'].includes(myInfo.companyCode);
  const { data: overtimeSummary } = useSWR<{ remainingHours: number }>(
    isOpen && isOvertimeVisible
      ? `/api/v1/passport/me/attendance/histories`
      : null,
    getNestedDataFetcher,
  );

  const remainingVacation = useMemo(() => {
    if (error && (error as AxiosError)) {
      const e = error as AxiosError;
      const data = e.response?.data as any;
      if (data && data.code === '0404') {
        return '';
      }

      return t('MYPAGE.조회 안됨');
    }

    if (!vacationData) {
      return t('MYPAGE.조회 안됨');
    }

    // 휴가 현황(PORTAL-11903)과 동일한 분 단위 계산 — 시간차/반차까지 반영해
    // 'N일 M시간' 으로 표기한다. 부여 휴가가 0 이면 임원으로 간주.
    const isExecutive = vacationGrantedMinutes(vacationData) === 0;

    return isExecutive
      ? ''
      : t('MYPAGE.잔여 {{value}}', {
          value: formatLeaveMinutes(vacationRemainingMinutes(vacationData)),
        });
  }, [vacationData, error]);

  const router = useRouter();
  const isMobile = router.route.startsWith(PAGE_URL.HOME_MOBILE);

  const subMenu: MyMenuContent[] = useMemo(
    () =>
      [
        MyMenuContent.PERSONAL_INFO,
        !isMobile && MyMenuContent.MAIL_SIGNATURE,
        MyMenuContent.LANGUAGE_SETTING,
        MyMenuContent.ALARM_SETTING,
        MyMenuContent.PASSWORD_SETTING,
      ].filter(Boolean) as MyMenuContent[],
    [isMobile],
  );

  const serviceMenu: MyMenuContent[] = useMemo(
    () =>
      [
        ['A', 'HC', 'N'].includes(myInfo.companyCode) &&
          MyMenuContent.CHECK_WORK_HOURS,
        isOvertimeVisible && MyMenuContent.CHECK_OVERTIME,
        MyMenuContent.CHECK_VACATION,
        MyMenuContent.CERTIFICATE_OF_EMPLOYMENT,
        MyMenuContent.PAY_STUB,
        // PORTAL-9807 [유저 서비스]바닐라닷컴 직원할인 인증 숨김 처리
        // MyMenuContent.SHOPPING_MALL_VERIFICATION,
        MyMenuContent.WIFI,
        // MyMenuContent.OLIVE,
        isMobile && MyMenuContent.WALLET,
        MyMenuContent.HEALTH_CARE_BOOKING,
        (['D180', '20013'].includes(myInfo.departmentCode) ||
          ['A20', 'T', 'HK'].includes(myInfo.companyCode)) &&
          MyMenuContent.PORTAL_APP_DOWNLOAD,
      ].filter(Boolean) as MyMenuContent[],
    [isMobile, myInfo.organizationCode, myInfo.companyCode],
  );

  return {
    menuData: [
      {
        title: '계정',
        subMenu: subMenu.map((menu) => ({
          menu,
          onClick: () => openSubMenu(menu),
          content: (() => {
            if (menu === MyMenuContent.LANGUAGE_SETTING) {
              return Language[language as LanguageValue];
            }
          })(),
        })),
      },
      {
        title: '직원 서비스',
        subMenu: serviceMenu.map((menu) => ({
          menu,
          onClick: () => {
            // 출입 정보는 앱 웹뷰에서 네이티브 화면으로 띄운다. (웹은 기존 서브화면 유지)
            if (menu === MyMenuContent.WALLET && isRnWebView()) {
              sendToRN(RnMessage.OPEN_CENTER_PASS);
              onClose();
              return;
            }

            // 서브메뉴 없이 포탈 밖 주소로 이동하는 항목 (건강관리실 예약 등)
            const externalUrl = MyMenuExternalUrlMap.get(menu);
            if (externalUrl) {
              openExternalUrl(externalUrl);
              return;
            }

            openSubMenu(menu);
          },
          content: (() => {
            if (menu === MyMenuContent.CHECK_WORK_HOURS) {
              return (
                WORKING_HOURS_OPTIONS.find((opt) => opt.value === workingHours)
                  ?.label ?? workingHours
              );
            } else if (menu === MyMenuContent.CHECK_OVERTIME) {
              return overtimeSummary
                ? `사용 가능 ${overtimeSummary.remainingHours}시간`
                : '';
            } else if (menu === MyMenuContent.CHECK_VACATION) {
              return remainingVacation;
            } else if (menu === MyMenuContent.OLIVE) {
              return currentStatus.title;
            }
          })(),
        })),
      },
    ],
    info: {
      ...myInfo,
      department: `${divisionName} • ${departmentName}`,
      isJobDescriptionLoading: isLoading,
      hasJobDescription: Boolean(
        passportData?.jobDescription?.summary &&
          passportData?.jobDescription?.content,
      ),
      jobDescription: passportData?.job ?? '',
      jobDescriptionSummary: passportData?.jobDescription?.summary || '-',
      jobDescriptionContent: passportData?.jobDescription?.content || '-',
    },
    onClickLogout: logout(onClose),
    onClickEditProfile: () => openSubMenu(MyMenuContent.EDIT_PROFILE),
  };
};

export default useMainProps;

const logout = (onClose: () => void) => {
  const dialog = useDialog();
  const router = useRouter();
  const reset = useResetAtom(MyInfoState, INIT_MY_INFO);
  const { t } = useTranslation();

  return () => {
    dialog.show({
      message: t('로그아웃 하시겠습니까?'),
      cancelButtonText: t('취소'),
      confirmButtonText: t('로그아웃'),
      onConfirm: async () => {
        const isMobile = router.route.startsWith(PAGE_URL.HOME_MOBILE);
        if (isMobile) {
          sendToRN(RnMessage.LOG_OUT);
        } else {
          await disableMessage();

          reset();

          // 로그아웃 시 공통 cleanup (쿠키, pending AI 요약 등)
          cleanupUserSession();

          onClose();

          location.href = `https://login.microsoftonline.com/common/oauth2/logout?post_logout_redirect_uri=${process.env.NEXT_PUBLIC_ORIGIN_URL}`;
        }
      },
    });
  };
};
